import { useEffect, useState } from "react";
import { Check, RotateCcw } from "lucide-react";
import { describeWhen } from "../../hooks/useFormDraft";

interface DraftBannerProps {
  hasDraft: boolean;
  draftSavedAt: Date | null;
  lastSavedAt: Date | null;
  onRestore: () => void;
  onDiscard: () => void;
}

/**
 * Offers an unsaved draft back, and otherwise quietly confirms that work is
 * being kept. Two states, never both.
 */
export default function DraftBanner({
  hasDraft,
  draftSavedAt,
  lastSavedAt,
  onRestore,
  onDiscard,
}: DraftBannerProps) {
  // "just now" would otherwise still be on screen ten minutes later, because
  // nothing re-renders this while the admin sits and thinks.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!lastSavedAt && !draftSavedAt) return;
    const id = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, [lastSavedAt, draftSavedAt]);

  if (hasDraft) {
    return (
      <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <RotateCcw className="h-4 w-4 shrink-0 text-amber-700" />
          <p className="flex-1 text-sm text-amber-900">
            <strong>You have unsaved work</strong> from {describeWhen(draftSavedAt)}.
            Restore it, or start fresh.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onRestore}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
            >
              Restore
            </button>
            <button
              type="button"
              onClick={onDiscard}
              className="rounded-md border border-amber-400 px-3 py-1.5 text-sm text-amber-900 hover:bg-amber-100"
            >
              Start fresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!lastSavedAt) return null;

  return (
    <p className="mb-3 flex items-center gap-1.5 text-xs text-gray-500">
      <Check className="h-3.5 w-3.5 text-green-600" />
      Draft kept in this browser · {describeWhen(lastSavedAt)}
    </p>
  );
}
